import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useTheme } from '@mui/material/styles';
import {
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Box,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Alert,
  Chip,
} from '@mui/material';
import Page from '../../components/Page';
import config from '../../config';
import CreditGauge from './CreditGauge';
import ComingSoonCard from './ComingSoonCard';
import CurrentPlanCard from './CurrentPlanCard';
import FeatureProgressCard from './FeatureProgressCard';
import { FEATURE_CATEGORIES, groupFeaturesByCategory } from './featureCategories';

// ----------------------------------------------------------------------

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  subtitle: PropTypes.string,
  color: PropTypes.string,
};

function StatCard({ title, value, subtitle, color }) {
  const theme = useTheme();
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {title}
        </Typography>
        <Typography variant="h3" sx={{ color: color || theme.palette.text.primary }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
};

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export default function Credits() {
  const { t } = useTranslation();
  const theme = useTheme();

  const [tabValue, setTabValue] = useState(0);
  const [remaining, setRemaining] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [cycles, setCycles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [remainingRes, subscriptionRes, usageRes, cyclesRes] = await Promise.all([
        axios.get(`${config.apiBaseUrl}/usage/remaining`),
        axios.get(`${config.apiBaseUrl}/usage/subscription`),
        axios.get(`${config.apiBaseUrl}/usage/usage`),
        axios.get(`${config.apiBaseUrl}/usage/cycles`),
      ]);
      setRemaining(remainingRes.data);
      setSubscription(subscriptionRes.data);
      setUsage(usageRes.data);
      setCycles(cyclesRes.data);
    } catch (err) {
      console.error('Failed to fetch credits data:', err);
      setError(err.response?.data?.message || err.message || t('credits.error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const renderSkeleton = () => (
    <Grid container spacing={3}>
      {[1, 2, 3, 4].map((i) => (
        <Grid item xs={12} sm={6} md={3} key={i}>
          <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
        </Grid>
      ))}
      <Grid item xs={12}>
        <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
      </Grid>
    </Grid>
  );

  // ---- Tab 0: Credit Dashboard ----
  const renderCategorySections = () => {
    const features = (usage && usage.features) || [];
    const byCategory = groupFeaturesByCategory(features);

    return FEATURE_CATEGORIES.map((cat) => {
      const featuresInCat = byCategory[cat.id];
      const categoryTotal = featuresInCat.reduce(
        (sum, f) => sum + (f.creditsConsumed || 0),
        0
      );

      if (cat.comingSoon) {
        return (
          <Grid item xs={12} key={cat.id}>
            <Typography variant="subtitle1" sx={{ mb: 1.5, mt: 1, fontWeight: 600 }}>
              {t(`credits.category.${cat.id}`)}
            </Typography>
            <ComingSoonCard categoryId={cat.id} variant="category" />
          </Grid>
        );
      }

      return (
        <Grid item xs={12} key={cat.id}>
          <Typography variant="subtitle1" sx={{ mb: 1.5, mt: 1, fontWeight: 600 }}>
            {t(`credits.category.${cat.id}`)}
          </Typography>
          <Grid container spacing={2}>
            {featuresInCat.map((feat) => (
              <Grid item xs={12} sm={6} md={4} key={feat.featureId}>
                {feat.comingSoon ? (
                  <ComingSoonCard featureId={feat.featureId} />
                ) : (
                  <FeatureProgressCard
                    featureId={feat.featureId}
                    runCount={feat.runCount}
                    creditsConsumed={feat.creditsConsumed}
                    categoryTotal={categoryTotal}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        </Grid>
      );
    });
  };

  const renderDashboard = () => {
    if (!remaining) return null;
    const {
      total: creditsTotal = 0,
      used: creditsUsed = 0,
      remaining: creditsRemaining = 0,
      overspendUsed: overspend = 0,
      cycleEnd,
    } = remaining;
    const isOverspending = overspend > 0;

    return (
      <Grid container spacing={3}>
        {subscription && (
          <Grid item xs={12}>
            <CurrentPlanCard subscription={subscription} />
          </Grid>
        )}

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('credits.gaugeTitle')}
              </Typography>
              <CreditGauge
                used={creditsUsed || 0}
                total={creditsTotal || 0}
                overspend={overspend || 0}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('credits.totalCredits')}
            value={(creditsTotal || 0).toLocaleString()}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('credits.usedCredits')}
            value={(creditsUsed || 0).toLocaleString()}
            color={isOverspending ? theme.palette.error.main : undefined}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('credits.remainingCredits')}
            value={(creditsRemaining || 0).toLocaleString()}
            color={creditsRemaining <= 0 ? theme.palette.error.main : theme.palette.success.main}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('credits.cycleEnd')}
            value={formatDate(cycleEnd)}
          />
        </Grid>

        {isOverspending && (
          <Grid item xs={12}>
            <Alert severity="warning">
              {t('credits.overspendWarning', { amount: overspend.toLocaleString() })}
            </Alert>
          </Grid>
        )}

        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mt: 2 }}>
            {t('credits.byCategoryTitle')}
          </Typography>
        </Grid>

        {renderCategorySections()}
      </Grid>
    );
  };

  // ---- Tab 1: Usage Summary ----
  const renderUsage = () => {
    if (!usage) return null;
    const { totalRunCount, totalCreditsConsumed, features } = usage;

    return (
      <Box>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <StatCard
              title={t('credits.totalRuns')}
              value={(totalRunCount || 0).toLocaleString()}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StatCard
              title={t('credits.totalConsumed')}
              value={(totalCreditsConsumed || 0).toLocaleString()}
            />
          </Grid>
        </Grid>

        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('credits.feature')}</TableCell>
                <TableCell align="right">{t('credits.runs')}</TableCell>
                <TableCell align="right">{t('credits.creditsConsumed')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {features && features.length > 0 ? (
                features.map((feat) => (
                  <TableRow key={feat.featureId}>
                    <TableCell>
                      {t(`credits.features.${feat.featureId}`, { defaultValue: feat.featureId })}
                    </TableCell>
                    <TableCell align="right">{(feat.runCount || 0).toLocaleString()}</TableCell>
                    <TableCell align="right">{(feat.creditsConsumed || 0).toLocaleString()}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      {t('credits.noData')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // ---- Tab 2: Cycle History ----
  const renderCycles = () => {
    const history = Array.isArray(cycles) ? cycles : [];
    const currentCycle = subscription && subscription.cycleStartedAt
      ? {
          cycleId: 'current',
          cycleStartedAt: subscription.cycleStartedAt,
          cycleEndedAt: subscription.cycleEndsAt,
          totalCredits: subscription.totalCredits,
          creditsUsed: subscription.creditsUsed,
          isCurrent: true,
        }
      : null;
    const cycleList = currentCycle ? [currentCycle, ...history] : history;

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('credits.cycleStart')}</TableCell>
              <TableCell>{t('credits.cycleEnd')}</TableCell>
              <TableCell align="right">{t('credits.totalCredits')}</TableCell>
              <TableCell align="right">{t('credits.usedCredits')}</TableCell>
              <TableCell align="center">{t('credits.status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cycleList.length > 0 ? (
              cycleList.map((cycle, idx) => {
                const isActive = !!cycle.isCurrent;
                return (
                  <TableRow key={cycle.cycleId || idx}>
                    <TableCell>{formatDate(cycle.cycleStartedAt)}</TableCell>
                    <TableCell>{formatDate(cycle.cycleEndedAt)}</TableCell>
                    <TableCell align="right">{(cycle.totalCredits || 0).toLocaleString()}</TableCell>
                    <TableCell align="right">{(cycle.creditsUsed || 0).toLocaleString()}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={isActive ? t('credits.active') : t('credits.completed')}
                        color={isActive ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    {t('credits.noData')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Page title={t('credits.pageTitle')}>
      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 3 }}>
          {t('credits.pageTitle')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          renderSkeleton()
        ) : (
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={tabValue}
                onChange={(_, newValue) => setTabValue(newValue)}
              >
                <Tab label={t('credits.tabDashboard')} />
                <Tab label={t('credits.tabUsage')} />
                <Tab label={t('credits.tabCycles')} />
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              {renderDashboard()}
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              {renderUsage()}
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              {renderCycles()}
            </TabPanel>
          </>
        )}
      </Container>
    </Page>
  );
}
